'use client'

import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, Search } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getIndependenceTier } from '@helper/utils/business-logic'
import cn from '@config/tailwind/cn'

import type { DateRangeValue } from '../../date-range-select'
import { TrendFilterHeader } from '../trend-filter-header'
import { LessonActivityChart, SubjectCompletionChart } from './charts'
import { Empty } from './empty'
import { StudentCard } from './student-card'

export { StudentCard } from './student-card'
export { LessonActivityChart, SubjectCompletionChart } from './charts'
export type { LessonTrendStudent } from './student-card'

export type LessonSort = 'name' | 'independence' | 'lastActive' | 'totalXp'

const SORT_ITEMS: Record<LessonSort, string> = {
  name: 'Nama',
  independence: 'Kemandirian',
  lastActive: 'Terakhir aktif',
  totalXp: 'Total XP',
}

type Summary = RouterOutputs['metrics']['getLessonTrend']
type StudentsPage = RouterOutputs['metrics']['getLessonTrendStudents']

interface LessonTrendCardsProps {
  type: 'web' | 'school'
  summary: Summary
  studentsQuery: UseInfiniteQueryResult<InfiniteData<StudentsPage>, unknown>
  sort: LessonSort
  onSortChange: (sort: LessonSort) => void
  className?: string
  renderProfileLink: RenderLink
  withSearch?: boolean
  withDownloadButton?: boolean
  renderCreateLink?: RenderLink
  /** Active Aktivitas sub-view + setter — drives the title + Membaca/Belajar pills in the shared header. */
  activityView?: 'lesson' | 'reading'
  onActivityViewChange?: (view: 'lesson' | 'reading') => void
  /** School-class filter value (`type === 'school'` only). `null` = all classes. Owned by the parent. */
  schoolClassId?: string | null
  /** Called when the user picks a class. When provided alongside `type === 'school'`, the class select is shown. */
  onSchoolClassChange?: (value: string | null) => void
  /** Resolved date range driving the trend. Owned by the parent so the query refetches on change. */
  dateRange?: DateRangeValue
  /** Called when the user changes the range. When provided, the date-range select is shown. */
  onDateRangeChange?: (range: DateRangeValue) => void
  /** Member is not assigned to any class — render a distinct empty state with no create CTA. */
  noClassAssigned?: boolean
  /** First name of the signed-in user, used to personalise the no-students empty state. */
  userName?: string
}

export function LessonTrendCards({
  type,
  summary,
  studentsQuery,
  sort,
  onSortChange,
  className,
  renderProfileLink,
  withSearch,
  withDownloadButton,
  renderCreateLink,
  activityView,
  onActivityViewChange,
  schoolClassId,
  onSchoolClassChange,
  dateRange,
  onDateRangeChange,
  noClassAssigned,
  userName,
}: LessonTrendCardsProps) {
  const [nameFilter, setNameFilter] = useState('')

  const loadedStudents = useMemo(
    () => studentsQuery.data?.pages.flatMap((page) => page.students) ?? [],
    [studentsQuery.data],
  )

  const visibleStudents = useMemo(() => {
    const q = nameFilter.trim().toLowerCase()
    if (!q) return loadedStudents
    return loadedStudents.filter((s) => s.name.toLowerCase().includes(q))
  }, [loadedStudents, nameFilter])

  // Infinite scroll: reveal the next page as the sentinel nears the viewport.
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = studentsQuery
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) void fetchNextPage()
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const downloadCSV = () => {
    if (loadedStudents.length === 0) {
      toast.error('Tidak ada data untuk diunduh.')
      return
    }
    if (hasNextPage) {
      toast.info('Hanya data yang sudah dimuat yang diunduh. Gulir untuk memuat lebih banyak.')
    }

    // Per-subject XP columns — union across loaded students, preserving first-seen (server) order.
    const subjectColumns: { subjectId: string; name: string }[] = []
    const seenSubjects = new Set<string>()
    for (const s of loadedStudents) {
      for (const sub of s.perSubjectXp) {
        if (!seenSubjects.has(sub.subjectId)) {
          seenSubjects.add(sub.subjectId)
          subjectColumns.push({ subjectId: sub.subjectId, name: sub.name })
        }
      }
    }

    const headers = [
      'Nama',
      'Terakhir aktif',
      'Kemandirian',
      'Belajar mandiri',
      'Tugas',
      'Latihan',
      'Total XP',
      ...subjectColumns.map((c) => c.name),
    ]

    const rows = loadedStudents.map((s) => {
      const { label } = getIndependenceTier({ tugasCount: s.tugasCount, latihanCount: s.latihanCount })
      // Mirrors the card headline: latihan/tugas multiplier, or 100% when there is no assigned work.
      const mandiri =
        s.tugasCount === 0
          ? s.latihanCount > 0
            ? '100%'
            : '-'
          : `${(s.latihanCount / s.tugasCount).toLocaleString('id-ID', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            })}x`
      const lastActive = s.lastActiveAt
        ? new Date(s.lastActiveAt).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })
        : 'Belum pernah login'
      const xpBySubject = new Map(s.perSubjectXp.map((x) => [x.subjectId, x.xp]))
      const subjectCells = subjectColumns.map((c) => xpBySubject.get(c.subjectId) ?? 0)
      return [
        `"${s.name}"`,
        `"${lastActive}"`,
        `"${label}"`,
        `"${mandiri}"`,
        s.tugasCount,
        s.latihanCount,
        s.totalXp,
        ...subjectCells,
      ].join(',')
    })

    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'tren-belajar.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // Member with no assigned class — no classes to filter, so skip the header and show a bare empty state.
  if (noClassAssigned) {
    return (
      <div className={cn('srd:w-full srd:max-w-[1200px] srd:mx-auto srd:px-4', className)}>
        <Empty type={type} noClass />
      </div>
    )
  }

  const hasStudents = summary.population.totalChildren > 0

  return (
    <div className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-8', className)}>
      <div className='srd:w-full srd:border-b srd:border-outline-variant/50 srd:pb-8'>
        <TrendFilterHeader
          type={type}
          activityView={activityView}
          onActivityViewChange={onActivityViewChange}
          schoolClassId={schoolClassId}
          onSchoolClassChange={onSchoolClassChange}
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
        />
      </div>

      <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-4 srd:max-w-[1200px] srd:mx-auto srd:px-4'>
        {summary.lastCalculatedAt && (
          <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-x-2 srd:gap-y-1'>
            <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>
              Terakhir diperbarui{' '}
              {new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(
                new Date(summary.lastCalculatedAt),
              )}
            </span>
          </div>
        )}

        {!hasStudents ? (
          <Empty
            type={type}
            renderLink={renderCreateLink}
            noStudents={!summary.hasAnyChildren}
            userName={userName}
            teamId={schoolClassId}
          />
        ) : (
          <>
            <div className='srd:grid srd:grid-cols-1 srd:items-stretch srd:lg:grid-cols-2 srd:gap-3'>
              <section className='srd:flex srd:flex-col srd:gap-2 srd:rounded-lg srd:border srd:border-grey-30 srd:bg-neutral-white srd:p-3'>
                <p className={cn(typographyVariants({ variant: 'title-md' }))}>Aktivitas belajar</p>
                <LessonActivityChart
                  data={summary.activityByDay}
                  population={summary.population}
                  from={dateRange?.from}
                  to={dateRange?.to}
                />
              </section>
              <section className='srd:flex srd:flex-col srd:gap-2 srd:rounded-lg srd:border srd:border-grey-30 srd:bg-neutral-white srd:p-3'>
                <p className={cn(typographyVariants({ variant: 'title-md' }))}>Penyelesaian tugas</p>
                <SubjectCompletionChart data={summary.subjectCompletion} population={summary.population} />
              </section>
            </div>

            {/* Controls: sort + search + download */}
            <div className='srd:flex srd:items-center srd:justify-between srd:gap-2 srd:flex-wrap'>
              <div className='srd:flex srd:items-center srd:gap-2 srd:flex-wrap srd:flex-1'>
                {withSearch && (
                  <InputGroup size='sm' className='srd:max-w-xs'>
                    <InputGroupAddon align='inline-start'>
                      <Search className='srd:text-blue-100 srd:size-4' />
                    </InputGroupAddon>
                    <InputGroupInput
                      placeholder='Cari nama...'
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                    />
                  </InputGroup>
                )}

                {withDownloadButton && (
                  <Button variant='ghost' className='srd:text-neutral-black' onClick={downloadCSV}>
                    <Download className='srd:size-4 srd:mr-2' />
                    Unduh
                  </Button>
                )}
              </div>

              <Select items={SORT_ITEMS} value={sort} onValueChange={(value) => onSortChange(value as LessonSort)}>
                <SelectTrigger size='sm' className='srd:w-auto'>
                  <SelectValue>Urutkan: {SORT_ITEMS[sort]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(SORT_ITEMS) as [LessonSort, string][]).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Card grid */}
            {studentsQuery.isPending ? (
              <div className='srd:grid srd:gap-3'>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className='srd:h-40 srd:w-full srd:rounded-xl' />
                ))}
              </div>
            ) : studentsQuery.isError ? (
              <div className='srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-3 srd:py-10 srd:text-center'>
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                  Gagal memuat data murid. Silakan coba lagi.
                </p>
                <Button variant='outline' size='sm' onClick={() => void studentsQuery.refetch()}>
                  Coba lagi
                </Button>
              </div>
            ) : visibleStudents.length === 0 ? (
              <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:py-10 srd:text-center')}>
                Tidak ada murid yang cocok dengan kata kunci "{nameFilter}".
              </p>
            ) : (
              <div className='srd:grid srd:gap-3'>
                {visibleStudents.map((student) => (
                  <StudentCard key={student.id} student={student} renderProfileLink={renderProfileLink} />
                ))}
              </div>
            )}

            {/* Infinite-scroll sentinel + loading indicator */}
            <div ref={sentinelRef} className='srd:h-px srd:w-full' />
            {isFetchingNextPage && (
              <div className='srd:grid srd:grid-cols-1 srd:xl:grid-cols-2 srd:gap-3'>
                {Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton key={i} className='srd:h-40 srd:w-full srd:rounded-xl' />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
